import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext.tsx";
import { userHasGlobalScope } from "../auth/scopes";
import { useTenant } from "../contexts/TenantContext.tsx";
import type { AgentSkill } from "../api/client.ts";

export interface SkillFormValues {
  tenant_id: string | null;
  slug: string;
  title: string;
  description: string;
  body: string;
  enabled: boolean;
}

// Mounted only while open, so each opening starts from `initial` (or blank).
interface Props {
  initial?: AgentSkill | null;
  onClose: () => void;
  onSubmit: (values: SkillFormValues) => void;
  saving: boolean;
}

const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{0,98}[a-z0-9]$/;

function SkillEditDialog({ initial, onClose, onSubmit, saving }: Props) {
  const { user } = useAuth();
  const { selectedTenantId, tenants } = useTenant();
  const canManageGlobal = userHasGlobalScope(user, "users:manage");

  const [tenantId, setTenantId] = useState<string | null>(
    initial ? initial.tenant_id : selectedTenantId,
  );
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);

  const slugValid = SLUG_REGEX.test(slug);
  const canSubmit =
    slugValid && title.trim().length > 0 && description.trim().length > 0 && body.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit || saving) return;
    onSubmit({ tenant_id: tenantId, slug, title, description, body, enabled });
  };

  const isEdit = !!initial;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? `Edit skill: ${initial?.title}` : "New skill"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Scope"
            select
            value={tenantId ?? "__global__"}
            onChange={(e) =>
              setTenantId(e.target.value === "__global__" ? null : e.target.value)
            }
            disabled={isEdit}
            helperText={
              isEdit
                ? "Scope is fixed after creation"
                : "Tenant skills are only visible inside that tenant; global skills are shared across all tenants."
            }
          >
            {canManageGlobal && <MenuItem value="__global__">Global (all tenants)</MenuItem>}
            {tenants.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            error={slug.length > 0 && !slugValid}
            helperText={
              slug.length > 0 && !slugValid
                ? "Lowercase letters, digits, hyphens. Must start and end with a letter or digit."
                : "Used as the MCP prompt name (e.g. research-icp)."
            }
            slotProps={{ htmlInput: { style: { fontFamily: "monospace" } } }}
          />
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <TextField
            label="When to use"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
            helperText="Short hint preloaded into the agent's system prompt. The agent uses this to decide when to load the full body."
          />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Instructions (markdown) — loaded on demand by the agent.
          </Typography>
          <TextField
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            minRows={14}
            maxRows={30}
            slotProps={{
              htmlInput: { style: { fontFamily: "monospace", fontSize: "0.875rem" } },
            }}
          />
          <FormControlLabel
            control={
              <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            }
            label="Enabled"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit} loading={saving}>
          {isEdit ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SkillEditDialog;
